/**
 * Helper functions for displaying consistent notifications throughout the app
 */

/**
 * Handle API response notifications
 * @param {Object} response - API response object
 * @param {Function} notifyFn - Notification function (from useNotification hook)
 * @param {string} successMsg - Optional custom success message
 * @param {string} errorMsg - Optional custom error message
 * @returns {boolean} - Whether the operation was successful
 */
export const handleApiResponse = (response, notifyFn, successMsg = null, errorMsg = null) => {
    if (response?.data?.success === true) {
        notifyFn.success(successMsg || response.data.msg || 'Operation successful');
        return true;
    } else {
        notifyFn.error(errorMsg || 'Operation failed');
        return false;
    }
};

/**
 * Handle API error notifications
 * @param {Error} error - Error object from catch block
 * @param {Function} notifyFn - Notification function (from useNotification hook)
 * @param {string} context - The context of the operation (e.g., 'login', 'profile', 'schedule')
 */
export const handleApiError = (error, notifyFn, context = '') => {
    const errorMessages = {
        login: 'Login failed',
        profile: 'Profile update failed',
        schedule: 'Schedule update failed',
        otp: 'OTP verification failed',
        default: 'Operation failed'
    };

    notifyFn.error(errorMessages[context] || errorMessages.default);
};

/**
 * Handle form validation errors
 * @param {Object} errors - Object with field names as keys and error messages as values
 * @param {Function} notifyFn - Notification function (from useNotification hook)
 * @param {string} context - The context of the form (e.g., 'profile', 'schedule')
 */
export const handleFormErrors = (errors, notifyFn, context = '') => {
    if (Object.keys(errors).length > 0) {
        const errorMessages = {
            profile: 'Please fill in all required fields',
            schedule: 'Please complete all schedule details',
            default: 'Please check your input'
        };
        notifyFn.error(errorMessages[context] || errorMessages.default);
    }
};

/**
 * Display a confirmation notification before proceeding with an action
 * @param {string} message - Confirmation message to display
 * @param {Function} notifyFn - Notification function (from useNotification hook)
 * @returns {boolean} - Whether the user confirmed
 */
export const confirmAction = (message, notifyFn) => {
    const confirmed = window.confirm(message);
    if (confirmed) {
        notifyFn.info('Proceeding with action');
    }
    return confirmed;
}; 